import { merge, Observable, timer } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { Select, Store } from '@ngxs/store';

import { NzDrawerService } from 'ng-zorro-antd/drawer';

import { ActionsState } from '@app/core/store/states/actions/actions.state';
import {
  GetPaymentMethods,
  SetPaymentMethodAsDefault,
} from '@app/core/store/states/customer/payment-methods/payment-methods-state.actions';
import { PaymentMethodsState } from '@app/core/store/states/customer/payment-methods/payment-methods.state';
import {
  PreviewCustomerSubscription,
  RestartCustomerSubscription,
  StartCustomerSubscription,
} from '@app/core/store/states/customer/subscription/subscription-state.actions';
import { StartSubscriptionDrawerComponent } from './components/start-drawer/start-subscription-drawer.component';
import { AdspertPlanType, InvoiceCurrency, SubscriptionInvoicePeriod } from '@app/core/models/customer.models';
import { SubscriptionState } from '@app/core/store/states/customer/subscription/subscription.state';

@Injectable()
export class SubscriptionService {
  @Select(ActionsState.whileAction([PreviewCustomerSubscription, GetPaymentMethods, StartCustomerSubscription]))
  readonly preloading$!: Observable<boolean>;

  @Select(ActionsState.whileAction(RestartCustomerSubscription))
  readonly restarting$!: Observable<boolean>;

  @Select(PaymentMethodsState.hasPaymentMethods)
  readonly hasPaymentMethods$!: Observable<boolean>;

  constructor(
    private dr: NzDrawerService,
    private store: Store,
  ) {}

  restartSubscription(): Observable<unknown> {
    return this.store.dispatch(new RestartCustomerSubscription());
  }

  openSubscriptionDrawer(): Observable<boolean> {
    const actions = [];

    if (this.store.selectSnapshot(SubscriptionState.isSelfServiceLegacy)) {
      actions.push(new PreviewCustomerSubscription());
    }

    if (!this.store.select(PaymentMethodsState.hasPaymentMethods)) {
      actions.push(new GetPaymentMethods());
    }

    return this.store.dispatch(actions).pipe(switchMap(() => this.createSubscriptionDrawer(0)));
  }

  onPaymentConfirmedStartSubscription(
    planType: AdspertPlanType,
    currency: InvoiceCurrency,
    period: SubscriptionInvoicePeriod,
  ): void {
    const [method] = this.store.selectSnapshot(PaymentMethodsState.methods);

    if (method) {
      if (!method.default) {
        this.store.dispatch(new SetPaymentMethodAsDefault(method.paymentMethodId));
      }

      this.store
        .dispatch(
          new StartCustomerSubscription(
            planType || AdspertPlanType.Essential,
            currency || 'USD',
            period || SubscriptionInvoicePeriod.Monthly,
          ),
        )
        .pipe(switchMap(() => this.createSubscriptionDrawer(3)))
        .subscribe();
    }
  }

  private createSubscriptionDrawer(startIdx: number): Observable<boolean> {
    const drawerRef = this.dr.create({
      nzContent: StartSubscriptionDrawerComponent,
      nzClosable: false,
      nzPlacement: 'right',
      nzWidth: '100%`',
      nzWrapClassName: 'a-bg-color-active adspt-drawer-full-width',
      nzMask: false,
      nzContentParams: {
        activeIdx: startIdx,
      },
    });

    return timer(1).pipe(
      switchMap(() => {
        const component = drawerRef.getContentComponent();
        component.drawerInit();

        return merge(drawerRef.afterClose, component.close$).pipe(
          take(1),
          tap(() => drawerRef.close()),
          map((result) => result === true),
        );
      }),
    );
  }
}
